import "./sentry";
import { registerUploadSW } from "./lib/swRegistration";

// Best-effort registration; don't await.
registerUploadSW();

import React, {StrictMode, useEffect, useState} from "react";
import {createRoot} from "react-dom/client";
import App from "./pages/App";
import Authentication from "./pages/auth/Authentication";
import Registration from "./pages/auth/Registration";
import RegistrationConfirm from "./pages/auth/RegistrationConfirm";
import {PasswordChange, RequestAccessLink} from "./pages/auth/ChangePassword";
import CustomRendererSandbox from "./templates";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { Navigate } from "react-router";
import { Provider } from "mobx-react";
import { rootStore } from "./stores";
import {BETA, DEV, getConfig} from "./lib/configMgr";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import OfflineDetect from "./components/OfflineDetect";
import * as Sentry from "@sentry/react";
import AdminPage from "./pages/Admin";
import MaintenancePage from "./pages/MaintenancePage";
import ErrorPage from "./pages/ErrorPage";
import GraphPlayground from "./pages/SigmaGraphPlayground";
import Help from "./pages/Help";
import DocsPage from "./pages/DocsPage";
import Verification from "./pages/auth/Verification";
import CompoundComparisonDemo from "./components/evidence/CompoundComparisonDemo";
import StreamingTestPage from "./pages/StreamingTestPage";
import NetworkTestPage from "./pages/NetworkTestPage";
import DeveloperPage from "./pages/developer/DeveloperPage";
import { buildV2Route, buildV2ArtifactRoute } from "./pages/workspace/routes";
import { prefersClassicUI } from "./lib/uiPreference";
import EntitlementsLayout from "./pages/admin-entitlements/EntitlementsLayout";
import EntitlementsDashboard from "./pages/admin-entitlements/Dashboard";
import EntitlementsSKUs from "./pages/admin-entitlements/Entitlements";
import EntUsers from "./pages/admin-entitlements/Users";
import EntUsage from "./pages/admin-entitlements/Usage";
import SkuCatalogLayout from "./pages/admin-sku-catalog/SkuCatalogLayout";
import SkuCatalog from "./pages/admin-sku-catalog/SkuCatalog";
import ReleaseNotesLayout from "./pages/admin-release-notes/ReleaseNotesLayout";
import ReleaseNotesAdmin from "./pages/admin-release-notes/ReleaseNotes";
import BroadcastsLayout from "./pages/admin-broadcasts/BroadcastsLayout";
import Broadcasts from "./pages/admin-broadcasts/Broadcasts";
import AccessAdminLayout from "./pages/admin-access/AccessAdminLayout";
import AccessConsole from "./pages/admin-access/AccessConsole";
import {
  Contracts as EntContracts,
  Teams as EntTeams,
  Audit as EntAudit,
} from "./pages/admin-entitlements/Stubs";
import {initTheme} from "./lib/themeManager";
import { handleUnhandledRejection } from "./lib/unhandledRejection";
export * from "./styles";

const {
  Sentry: sentryOptions,
  withV2 = false,
} = getConfig();

// On cells where v2 is the default, land on /v2 unless the user chose classic v1.
function RootGate() {
  if (withV2 && !prefersClassicUI()) {
    return <Navigate to="/v2" replace />;
  }
  return <App />;
}

// Handle Vite chunk preload failures (stale cache after deployment) with a one-shot reload
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('chunkReloadAttempted')) {
    sessionStorage.setItem('chunkReloadAttempted', '1');
    window.location.reload();
  }
});

// Suppress benign ResizeObserver errors that don't affect functionality
// These occur when ResizeObserver callbacks trigger faster than the browser can process them
const resizeObserverErrorHandler = (event) => {
  const message = event.message || event.error?.message || '';
  if (message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
};
window.addEventListener('error', resizeObserverErrorHandler);

// Capture unhandled Promise rejections for PostHog error tracking
window.addEventListener('unhandledrejection', handleUnhandledRejection);

// Also suppress via console intercept as last resort
const originalError = console.error;
console.error = (...args) => {
  const message = args[0]?.toString() || '';
  if (message.includes('ResizeObserver loop')) {
    return; // Suppress
  }
  originalError.apply(console, args);
};

const createRouter = sentryOptions.DSN
                     ? Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
                     : createBrowserRouter;


const router = createRouter(
  [
    {
      path: "/",
      element: <RootGate />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/search",
      element: <App />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/demo",
      element: <Navigate to={"/"} replace />,
    },
    {path: "graph", element: DEV ? <GraphPlayground /> : <Navigate to={"/"} replace />},
    {path: "compound-comparison-demo", element: DEV ? <CompoundComparisonDemo /> : <Navigate to={"/"} replace />},
    {path: "help", element: <Help />},
    {path: "docs", element: <DocsPage />},
    {path: "admin", element: <AdminPage />},
    {
      path: "admin/entitlements",
      element: <EntitlementsLayout />,
      errorElement: <ErrorPage />,
      children: [
        {index: true,         element: <EntitlementsDashboard />},
        {path: "skus",        element: <EntitlementsSKUs />},
        {path: "users",       element: <EntUsers />},
        {path: "usage",       element: <EntUsage />},
        {path: "contracts",   element: <EntContracts />},
        {path: "teams",       element: <EntTeams />},
        {path: "audit",       element: <EntAudit />},
      ],
    },
    {
      path: "admin/sku-catalog",
      element: <SkuCatalogLayout />,
      errorElement: <ErrorPage />,
      children: [
        {index: true, element: <SkuCatalog />},
      ],
    },
    {
      path: "admin/release-notes",
      element: <ReleaseNotesLayout />,
      errorElement: <ErrorPage />,
      children: [
        {index: true, element: <ReleaseNotesAdmin />},
      ],
    },
    {
      path: "admin/broadcasts",
      element: <BroadcastsLayout />,
      errorElement: <ErrorPage />,
      children: [
        {index: true, element: <Broadcasts />},
      ],
    },
    {
      path: "admin/access",
      element: <AccessAdminLayout />,
      errorElement: <ErrorPage />,
      children: [
        {index: true, element: <AccessConsole />},
      ],
    },
    {path: "streaming-test", element: <StreamingTestPage />},
    {path: "network-test", element: <NetworkTestPage />},
    {path: "signin", element: <Authentication />},
    {path: "verify", element: <Verification />},
    {path: "register", element: <Registration />},
    {path: "registration/confirm", element: <RegistrationConfirm />},
    {path: "changepass", element: <PasswordChange />},
    {path: "resetpass", element: <RequestAccessLink />},
    {path: "sandbox", element: (DEV || BETA) ? <CustomRendererSandbox /> : <Navigate to={"/"} replace />},
    {path: "maintenance", element: <MaintenancePage />},
    {path: "developer", element: <DeveloperPage />},
    buildV2Route(withV2),
    buildV2ArtifactRoute(withV2),
    {path: "*", element: <Navigate to={"/"} replace />},
  ]
);

// Initialize theme before rendering to prevent flash
initTheme();

const strict_mode = true;
const root = createRoot(document.getElementById('root'));
// Lazy mount of the chaos dev panel — outside the auth wrapper so it
// renders on the login screen too (a Playwright/E2E run without a real
// session can still verify the panel + its presets).
const DevChaosPanelGate = () => {
  const [Panel, setPanel] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("chaos") !== "1") return;
    let cancelled = false;
    Promise.all([
      import("./dev/chaos/ChaosPanel"),
      import("./dev/chaos/ChaosPanel.css"),
    ]).then(([mod]) => {
      if (!cancelled) setPanel(() => mod.default);
    }).catch((e) => console.warn("[chaos] panel load failed", e));
    return () => { cancelled = true; };
  }, []);
  return Panel ? <Panel /> : null;
};

const content = (
  <GlobalErrorBoundary>
    <Provider {...rootStore} >
      <RouterProvider router={router} />
      <OfflineDetect />
      <DevChaosPanelGate />
    </Provider>
  </GlobalErrorBoundary>
);

if (strict_mode) {
  root.render(<StrictMode>{content}</StrictMode>);
}
else {
  root.render(content);
}
